// Iter v34ci — Halaman Admin: Kelola Karyawan (edit data + reset wajah 5-pose).
// Butuh perm employees.admin_edit untuk edit data dan employees.face_manage untuk wajah.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Search, Edit3, ScanFace, RefreshCw, X, Camera, CheckCircle2, Users, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, Card, Button, Input, Select, Skeleton, Modal, Badge } from "@/components/ui-kit";
import { loadFaceApi, getCamera, stopStream, detectFace, detectBlink, snapshotVideo } from "@/lib/faceApi";

const POSES = [
  { key: "front", label: "Menghadap Depan", tip: "Tatap lurus ke kamera, wajah di tengah oval" },
  { key: "left",  label: "Miring ke Kiri",  tip: "Putar kepala ~30° ke KIRI karyawan" },
  { key: "right", label: "Miring ke Kanan", tip: "Putar kepala ~30° ke KANAN karyawan" },
  { key: "up",    label: "Angkat Dagu",     tip: "Miringkan wajah ke atas ~15°" },
  { key: "down",  label: "Turunkan Dagu",   tip: "Miringkan wajah ke bawah ~15°" },
];

export default function AdminKelolaKaryawan() {
  const { hasPerm } = useAuth();
  const canFace = hasPerm("employees.face_manage");
  const canEdit = hasPerm("employees.admin_edit");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [department, setDepartment] = useState("");
  const [departments, setDepartments] = useState([]);

  // Edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editEmp, setEditEmp] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  // Face wizard
  const [faceOpen, setFaceOpen] = useState(false);
  const [faceEmp, setFaceEmp] = useState(null);
  const [faReady, setFaReady] = useState(false);
  const [faceStream, setFaceStream] = useState(null);
  const [poseIdx, setPoseIdx] = useState(0);
  const [captured, setCaptured] = useState([]); // [{ pose, descriptor, photo_b64 }]
  const [livenessOk, setLivenessOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [e, d] = await Promise.all([
        api.get("/employees"),
        api.get("/department-categories").catch(() => ({ data: [] })),
      ]);
      setRows(e.data || []);
      setDepartments(d.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal muat"); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (department && r.department !== department) return false;
      if (!qq) return true;
      return (
        (r.name || "").toLowerCase().includes(qq) ||
        (r.employee_code || "").toLowerCase().includes(qq) ||
        (r.email || "").toLowerCase().includes(qq) ||
        (r.nik || "").toLowerCase().includes(qq) ||
        (r.department || "").toLowerCase().includes(qq)
      );
    });
  }, [rows, q, department]);

  const openEdit = (emp) => {
    setEditEmp(emp);
    setEditForm({
      name: emp.name || "",
      nik: emp.nik || "",
      phone: emp.phone || "",
      address: emp.address || "",
      email: emp.email || "",
      department: emp.department || "",
      position: emp.position || "",
      status: emp.status || "active",
      gaji_harian: emp.gaji_harian || 0,
      uang_makan: emp.uang_makan || 0,
      bank_name: emp.bank_name || "",
      bank_account_holder: emp.bank_account_holder || "",
      bank_account_number: emp.bank_account_number || "",
      overtime_enabled: emp.overtime_enabled ?? true,
      allow_debt: !!emp.allow_debt,
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editEmp) return;
    setEditSaving(true);
    try {
      await api.patch(`/employees/${editEmp.id}`, editForm);
      toast.success("Data karyawan tersimpan");
      setEditOpen(false);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
    finally { setEditSaving(false); }
  };

  // ------------ FACE WIZARD ------------
  const openFaceWizard = async (emp) => {
    setFaceEmp(emp);
    setCaptured([]);
    setPoseIdx(0);
    setLivenessOk(false);
    setStatusMsg("Memuat model wajah…");
    setFaceOpen(true);
    try {
      await loadFaceApi((m) => setStatusMsg(m));
      setFaReady(true); setStatusMsg("Model siap. Klik 'Nyalakan Kamera'.");
    } catch (e) { setStatusMsg("Gagal load model: " + (e?.message || "")); }
  };

  const startCam = async () => {
    try {
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      const s = await getCamera(videoRef.current, { facingMode: "user" });
      setFaceStream(s);
      setStatusMsg("Kamera aktif. Klik 'Cek Liveness' untuk pose depan.");
    } catch (e) { toast.error(e?.message || "Gagal buka kamera"); }
  };
  const stopCam = () => { stopStream(faceStream); setFaceStream(null); };

  const runLiveness = async () => {
    setLivenessOk(false); setBusy(true);
    setStatusMsg("Kedipkan mata jelas ~2x dalam 8 detik…");
    try {
      const r = await detectBlink(videoRef.current, { timeoutMs: 8000 });
      if (r?.blinkDetected || r?.ok) {
        setLivenessOk(true);
        setStatusMsg("Liveness OK ✓. Klik 'Ambil Pose' sekarang.");
        toast.success("Liveness berhasil");
      } else {
        setStatusMsg("Liveness gagal — coba lagi (pencahayaan cukup, kedipkan jelas).");
        toast.error("Liveness gagal");
      }
    } catch (e) { toast.error(e?.message || "Error"); }
    finally { setBusy(false); }
  };

  const capturePose = async () => {
    const currentPose = POSES[poseIdx];
    if (currentPose.key === "front" && !livenessOk) {
      toast.error("Selesaikan liveness dulu (pose depan)");
      return;
    }
    setBusy(true);
    try {
      const det = await detectFace(videoRef.current);
      if (!det) { toast.error("Wajah tidak terdeteksi"); return; }
      const photo = snapshotVideo(videoRef.current, canvasRef.current, 0.7);
      const entry = { pose: currentPose.key, descriptor: Array.from(det.descriptor), photo_b64: photo || "" };
      setCaptured((prev) => {
        const cleaned = prev.filter((x) => x.pose !== currentPose.key);
        return [...cleaned, entry];
      });
      toast.success(`Pose "${currentPose.label}" ✓ (${poseIdx + 1}/${POSES.length})`);
      if (poseIdx < POSES.length - 1) {
        setPoseIdx(poseIdx + 1);
        setStatusMsg(`Pose berikut: ${POSES[poseIdx + 1].label}. ${POSES[poseIdx + 1].tip}`);
      } else {
        setStatusMsg("5 pose selesai. Klik 'Simpan Wajah' untuk mendaftarkan.");
      }
    } catch (e) { toast.error(e?.message || "Gagal ambil pose"); }
    finally { setBusy(false); }
  };

  const submitFace = async () => {
    if (!faceEmp) return;
    if (captured.length < POSES.length) { toast.error(`Baru ${captured.length}/${POSES.length} pose`); return; }
    setBusy(true);
    try {
      const front = captured.find((c) => c.pose === "front");
      const payload = {
        face_descriptors_multi: captured,
        face_descriptor: front?.descriptor || null,
        face_photo_b64: front?.photo_b64 || "",
      };
      await api.post(`/employees/${faceEmp.id}/face`, payload);
      toast.success(`Wajah ${faceEmp.name} berhasil didaftarkan (5 pose)`);
      stopCam();
      setFaceOpen(false);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan wajah"); }
    finally { setBusy(false); }
  };

  const resetFaceOnServer = async (emp) => {
    if (!window.confirm(`Hapus data wajah ${emp.name}? Karyawan harus daftar ulang wajah.`)) return;
    try {
      await api.delete(`/employees/${emp.id}/face`);
      toast.success("Data wajah dihapus");
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal hapus wajah"); }
  };

  useEffect(() => () => { stopStream(faceStream); }, [faceStream]);

  return (
    <div className="min-h-full bg-paper p-4 md:p-6 lg:p-8">
      <div className="max-w-[1400px] mx-auto space-y-6">
        <PageHeader
          overline="HR · Admin"
          title="Kelola Karyawan"
          description="Edit data karyawan lengkap dan kelola pendaftaran wajah (5 pose) langsung dari halaman admin."
        />

        <Card className="p-0">
          <div className="p-4 md:p-5 border-b border-stone-100 flex flex-wrap items-center gap-3">
            <Users className="h-4 w-4 text-stone-600"/>
            <h3 className="text-sm font-semibold text-stone-800 mr-auto">Daftar Karyawan <Badge className="bg-stone-100 text-stone-600 text-[10px] ml-2">{filtered.length}</Badge></h3>
            <div className="flex items-center gap-2">
              <Select value={department} onChange={(e) => setDepartment(e.target.value)} className="!py-1.5 !text-xs !w-40" data-testid="admin-kelola-dept">
                <option value="">Semua Departemen</option>
                {departments.map((d) => <option key={d.id || d.name} value={d.name}>{d.name}</option>)}
              </Select>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-stone-400"/>
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama / kode / NIK…" className="!py-1.5 !text-xs !pl-7 !w-56" data-testid="admin-kelola-search"/>
              </div>
              <button onClick={loadAll} className="text-stone-400 hover:text-stone-800" title="Refresh"><RefreshCw className="h-3.5 w-3.5"/></button>
            </div>
          </div>
          {loading ? (
            <div className="p-4 space-y-2">{Array.from({length:5}).map((_,i) => <Skeleton key={i} className="h-12"/>)}</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-stone-400 text-sm">Tidak ada karyawan sesuai filter.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-kelola-table">
                <thead className="bg-stone-50 text-left text-[11px] uppercase tracking-widest text-stone-500 border-b border-stone-100">
                  <tr>
                    <th className="py-2.5 px-3">Kode</th>
                    <th className="py-2.5 px-3">Nama</th>
                    <th className="py-2.5 px-3">Departemen</th>
                    <th className="py-2.5 px-3">Jabatan</th>
                    <th className="py-2.5 px-3">Status</th>
                    <th className="py-2.5 px-3">Wajah</th>
                    <th className="py-2.5 px-3 text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const hasFace = !!(r.face_descriptor || (r.face_descriptors_multi && r.face_descriptors_multi.length));
                    const poseCount = (r.face_descriptors_multi || []).length;
                    return (
                      <tr key={r.id} className="border-b border-stone-100 hover:bg-stone-50/70" data-testid={`admin-kelola-row-${r.id}`}>
                        <td className="py-2 px-3 font-mono text-[11px]">{r.employee_code || "-"}</td>
                        <td className="py-2 px-3">
                          <div className="font-medium text-stone-900">{r.name || "-"}</div>
                          <div className="text-[10px] text-stone-500">{r.email || ""}</div>
                        </td>
                        <td className="py-2 px-3 text-[12px] text-stone-700">{r.department || "-"}</td>
                        <td className="py-2 px-3 text-[12px] text-stone-700">{r.position || <span className="text-stone-400 italic">-</span>}</td>
                        <td className="py-2 px-3"><Badge className={r.status === "active" ? "bg-emerald-100 text-emerald-800 text-[10px]" : "bg-amber-100 text-amber-800 text-[10px]"}>{r.status || "-"}</Badge></td>
                        <td className="py-2 px-3">
                          {hasFace ? (
                            <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">✓ {poseCount || 1} pose</Badge>
                          ) : (
                            <Badge className="bg-stone-100 text-stone-500 text-[10px]">— belum</Badge>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right space-x-2">
                          {canEdit && (
                            <button onClick={() => openEdit(r)} className="text-blue-700 hover:text-blue-900 text-xs font-medium inline-flex items-center gap-1" data-testid={`admin-kelola-edit-${r.id}`}>
                              <Edit3 className="h-3.5 w-3.5"/> Edit Data
                            </button>
                          )}
                          {canFace && (
                            <button onClick={() => openFaceWizard(r)} className="text-emerald-700 hover:text-emerald-900 text-xs font-medium inline-flex items-center gap-1" data-testid={`admin-kelola-face-${r.id}`}>
                              <ScanFace className="h-3.5 w-3.5"/> {hasFace ? "Daftar Ulang Wajah" : "Daftar Wajah"}
                            </button>
                          )}
                          {canFace && hasFace && (
                            <button onClick={() => resetFaceOnServer(r)} className="text-red-700 hover:text-red-900 text-xs inline-flex items-center gap-1" title="Hapus wajah" data-testid={`admin-kelola-face-reset-${r.id}`}>
                              <Trash2 className="h-3.5 w-3.5"/>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* Edit Data Karyawan Modal */}
      <Modal open={editOpen} onClose={() => setEditOpen(false)} size="xl" title={editEmp ? `Edit Data: ${editEmp.name}` : "Edit Karyawan"}>
        {editEmp && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="admin-kelola-edit-modal">
            <F label="Nama"><Input value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} data-testid="edit-emp-name"/></F>
            <F label="NIK (16 digit)"><Input value={editForm.nik} onChange={(e) => setEditForm({...editForm, nik: e.target.value})} data-testid="edit-emp-nik"/></F>
            <F label="Email"><Input value={editForm.email} onChange={(e) => setEditForm({...editForm, email: e.target.value})} data-testid="edit-emp-email"/></F>
            <F label="No HP"><Input value={editForm.phone} onChange={(e) => setEditForm({...editForm, phone: e.target.value})} data-testid="edit-emp-phone"/></F>
            <F label="Alamat" full><Input value={editForm.address} onChange={(e) => setEditForm({...editForm, address: e.target.value})} data-testid="edit-emp-address"/></F>
            <F label="Departemen">
              <Select value={editForm.department} onChange={(e) => setEditForm({...editForm, department: e.target.value})} data-testid="edit-emp-department">
                <option value="">— pilih —</option>
                {departments.map((d) => <option key={d.id || d.name} value={d.name}>{d.name}</option>)}
              </Select>
            </F>
            <F label="Jabatan"><Input value={editForm.position} onChange={(e) => setEditForm({...editForm, position: e.target.value})} data-testid="edit-emp-position"/></F>
            <F label="Status">
              <Select value={editForm.status} onChange={(e) => setEditForm({...editForm, status: e.target.value})} data-testid="edit-emp-status">
                <option value="active">Aktif</option>
                <option value="pending_verification">Menunggu Verifikasi</option>
                <option value="inactive">Nonaktif</option>
              </Select>
            </F>
            <F label="Gaji Harian (Rp)"><Input type="number" value={editForm.gaji_harian || 0} onChange={(e) => setEditForm({...editForm, gaji_harian: Number(e.target.value) || 0})} data-testid="edit-emp-gaji"/></F>
            <F label="Uang Makan/hari (Rp)"><Input type="number" value={editForm.uang_makan || 0} onChange={(e) => setEditForm({...editForm, uang_makan: Number(e.target.value) || 0})} data-testid="edit-emp-um"/></F>
            <F label="Bank"><Input value={editForm.bank_name} onChange={(e) => setEditForm({...editForm, bank_name: e.target.value})} data-testid="edit-emp-bank-name"/></F>
            <F label="Nama Rekening"><Input value={editForm.bank_account_holder} onChange={(e) => setEditForm({...editForm, bank_account_holder: e.target.value})} data-testid="edit-emp-bank-holder"/></F>
            <F label="No Rekening"><Input value={editForm.bank_account_number} onChange={(e) => setEditForm({...editForm, bank_account_number: e.target.value})} data-testid="edit-emp-bank-no"/></F>
            <F label="Lembur Diaktifkan?">
              <Select value={editForm.overtime_enabled ? "true" : "false"} onChange={(e) => setEditForm({...editForm, overtime_enabled: e.target.value === "true"})} data-testid="edit-emp-ot">
                <option value="true">Ya</option>
                <option value="false">Tidak</option>
              </Select>
            </F>
            <F label="Boleh Kasbon/Hutang?">
              <Select value={editForm.allow_debt ? "true" : "false"} onChange={(e) => setEditForm({...editForm, allow_debt: e.target.value === "true"})} data-testid="edit-emp-debt">
                <option value="false">Tidak</option>
                <option value="true">Ya</option>
              </Select>
            </F>
            <div className="md:col-span-2 flex justify-end gap-2 pt-4 border-t border-stone-100">
              <Button variant="secondary" onClick={() => setEditOpen(false)}>Batal</Button>
              <Button onClick={saveEdit} disabled={editSaving} data-testid="edit-emp-save">
                {editSaving ? "Menyimpan…" : "Simpan"}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Face Wizard Modal (5-pose) */}
      <Modal open={faceOpen} onClose={() => { stopCam(); setFaceOpen(false); }} size="xl" title={faceEmp ? `Daftar Wajah: ${faceEmp.name}` : "Daftar Wajah"}>
        {faceEmp && (
          <div className="space-y-4" data-testid="admin-face-wizard">
            <div className="text-[11px] text-stone-600 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <b>Petunjuk:</b> Panggil karyawan ke depan perangkat. Ambil 5 pose berbeda (depan, kiri, kanan, angkat dagu, turun dagu). Pose depan wajib lolos liveness (kedipkan mata).
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="aspect-square bg-stone-900 rounded-xl overflow-hidden relative flex items-center justify-center">
                  <video ref={videoRef} playsInline muted autoPlay className="max-h-full max-w-full object-contain" style={{ transform: "scaleX(-1)" }}/>
                  <canvas ref={canvasRef} className="hidden"/>
                  {!faceStream && (
                    <div className="absolute inset-0 flex items-center justify-center text-stone-400 text-xs">Kamera belum aktif</div>
                  )}
                </div>
                <div className="mt-2 text-[11px] text-stone-600 min-h-[36px]">{statusMsg}</div>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {!faceStream ? (
                    <Button onClick={startCam} disabled={!faReady} data-testid="wizard-start-cam">
                      <Camera className="h-3.5 w-3.5"/> Nyalakan Kamera
                    </Button>
                  ) : (
                    <>
                      {POSES[poseIdx]?.key === "front" && !livenessOk && (
                        <Button onClick={runLiveness} disabled={busy} data-testid="wizard-liveness">Cek Liveness</Button>
                      )}
                      <Button onClick={capturePose} disabled={busy || (POSES[poseIdx]?.key === "front" && !livenessOk)} data-testid="wizard-capture-pose">
                        Ambil Pose {POSES[poseIdx]?.label || ""}
                      </Button>
                      <Button variant="secondary" onClick={stopCam} data-testid="wizard-stop-cam">Matikan</Button>
                    </>
                  )}
                </div>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-500 mb-2">Progress ({captured.length}/{POSES.length})</p>
                <div className="grid grid-cols-1 gap-2">
                  {POSES.map((p, i) => {
                    const done = captured.find((x) => x.pose === p.key);
                    const cur = i === poseIdx && captured.length < POSES.length;
                    return (
                      <div key={p.key} className={`p-2.5 rounded-lg border flex items-center gap-2 ${done ? "border-emerald-300 bg-emerald-50" : cur ? "border-terra-400 bg-terra-50" : "border-stone-200 bg-white"}`} data-testid={`wizard-pose-${p.key}`}>
                        {done ? <CheckCircle2 className="h-4 w-4 text-emerald-600"/> : <div className="h-4 w-4 rounded-full border-2 border-stone-300"/>}
                        <div className="flex-1 text-xs">
                          <div className="font-medium text-stone-900">{i+1}. {p.label}</div>
                          <div className="text-[10px] text-stone-500">{p.tip}</div>
                        </div>
                        {done?.photo_b64 && <img src={done.photo_b64} alt={p.label} className="h-10 w-10 rounded object-cover"/>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="flex justify-between items-center pt-3 border-t border-stone-100">
              <Button variant="secondary" onClick={() => { setCaptured([]); setPoseIdx(0); setLivenessOk(false); }} data-testid="wizard-reset">
                <X className="h-3.5 w-3.5"/> Ulang
              </Button>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => { stopCam(); setFaceOpen(false); }}>Tutup</Button>
                <Button onClick={submitFace} disabled={busy || captured.length < POSES.length} data-testid="wizard-submit-face">
                  <ScanFace className="h-3.5 w-3.5"/> Simpan Wajah ({captured.length}/{POSES.length})
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function F({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-600 mb-1">{label}</p>
      {children}
    </div>
  );
}
